import {forwardRef, useRef, useState, useEffect, useImperativeHandle} from 'react'
import {cx, tv} from '@local/utils/cx'
import {VariantProps} from 'tailwind-variants'

const dropdownStyles = tv({
	slots: {
		base: cx(
			'bg-transparent',
			'border border-grey-1 rounded-xs',
			'h-44',
			'text-neutral-100',
			'cursor-pointer',
			'relative',
			'col-span-2',
			'bg-transparent flex items-center  px-10 h-48 text-12',
			'rounded-xxs border border-grey-2 peer-focus-visible:border-grey-1',
			'peer-checked:border-white transition-colors duration-200 ease-quart-out hover:cursor-pointer',
			'peer-invalid:border-red',
		),
		optionLabel: cx(
			'text-12',

		),
		option: cx(
			'hover:bg-grey-5 px-10 py-10 hover:text-white-off col-span-2',
		),
		optionsContainer: cx(
			'absolute',
			'w-full',
			'left-0',
			'mt-5',
			'z-10',
			'max-h-120',
			'overflow-auto',
			'border',
			'rounded-xs',
			'top-full',
			'bg-grey-4',
			'animation-opacity ease-quart-out duration-5300',
		),
	},
	variants: {
		open: {
			true: {
				optionsContainer: 'block',
				base: 'border-green-500',
			},
			false: {
				optionsContainer: 'hidden',
			},
		},
	},
})

type DropdownVariantProps = VariantProps<typeof dropdownStyles>;

export interface Option {
	label: string;
	value: string;
}

interface DropdownProps extends DropdownVariantProps {
	id: string;
	name: string;
	options: Option[];
	placeholder?: string;
	dropdownClassName?: string,
	defaultValue?: Option,
	onSelect?: (value: any) => void;
}

export const Dropdown = forwardRef((
	{
		id,
		name,
		options,
		dropdownClassName,
		placeholder,
		defaultValue,
		onSelect,
		...props
	}: DropdownProps,
	ref,
) => {
	const [isOpen, setIsOpen] = useState(false)
	const [selected, setSelected] = useState<Option>(defaultValue)

	const dropdownRef = useRef<HTMLDivElement>(null)

	const handleSelect = (option: Option) => {
		setSelected(option)
		setIsOpen(false)
		onSelect?.(option.value)
	}

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false)
			}
		}

		const handleScroll = (event: Event) => {
			if (isOpen) {
				event.stopPropagation()
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('wheel', handleScroll, {passive: false})

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('wheel', handleScroll)
		}
	}, [isOpen, dropdownRef])

	useImperativeHandle(ref, () => ({
		getSelected: () => selected,
	}))

	const {base: baseClassName, optionLabel: optionLabelClassname, optionsContainer: optionsContainerClassName, option: optionClassName} = dropdownStyles({
		open: isOpen,
	})

	const optionsContainerDynamicStyles = cx(
		optionsContainerClassName(),
		'transition-opacity ease-linear duration-1300',
		isOpen ? 'opacity-100 visible' : 'opacity-0 invisible absolute')

	return (
		<div ref={dropdownRef} className={cx(baseClassName(), dropdownClassName)} onClick={() => setIsOpen(!isOpen)} {...props}>
			{selected?.value &&
				<label
					htmlFor={id || name}
					className={cx(
						'absolute top-0 ml-5 bg-grey-4 px-5 -translate-y-1/2 z-2 origin-left peer-focus:top-0 pointer-events-none left-0 mr-1',
						'top-0',

					)}>
					{placeholder}
				</label>}
			<input hidden id={id} name={name} value={selected?.value} />
			<span className={cx(optionLabelClassname())}>{selected?.label || placeholder}</span>
			<div className={cx(optionsContainerDynamicStyles)}>
				{options.map((option, index) => (
					<div key={index} className={cx(optionClassName())} onClick={() => handleSelect(option)}>
						{option.label}
					</div>
				))}
			</div>
		</div>
	)
})
